import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Download, Loader2, PhoneCall, RefreshCw, ShieldAlert, Smartphone, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { CallingLockedCard } from '@/features/calling/components/CallingLockedCard'
import { syncCallLog } from '@/features/calling/sim/callLogSync'
import { ensureSimPermissions } from '@/features/calling/sim/simCallFlow'
import { simStorage } from '@/features/calling/sim/simStorage'
import {
  callingKeys,
  useCallingMe,
  useConfirmPhoneVerification,
  useStartPhoneVerification,
  useUpdateMyPhone,
} from '@/hooks/useCalling'
import { SimCall } from '@/native/simCall'
import { removeDevice } from '@/services/api/calling.service'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { CallMode } from '@/types/api/calling'
import { formatDateTime } from '@/utils/date'
import { getErrorMessage } from '@/utils/error-parser'
import { isNativeApp } from '@/utils/platform'

/** The agent's own calling setup: number and its verification, preferred mode, the Android app. */
export function MyPhonePage() {
  const { data: me, isLoading } = useCallingMe()

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">My phone</h1>
        <p className="mt-1 text-neutral-500">The number Exotel rings when you call a lead, and calling from your phone.</p>
      </div>
      {isLoading || !me ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : !me.featureEnabled ? (
        <CallingLockedCard />
      ) : (
        <>
          <PhoneNumberCard />
          <PhoneAppCard />
        </>
      )}
    </div>
  )
}

function PhoneNumberCard() {
  const { data: me } = useCallingMe()
  const update = useUpdateMyPhone()
  const startVerification = useStartPhoneVerification()
  const confirm = useConfirmPhoneVerification()
  const [phone, setPhone] = useState(me?.profile?.phone ?? '')
  const [mode, setMode] = useState<CallMode>(me?.profile?.preferredMode ?? 'CLICK_TO_CALL')

  useEffect(() => {
    setPhone(me?.profile?.phone ?? '')
    setMode(me?.profile?.preferredMode ?? 'CLICK_TO_CALL')
  }, [me?.profile?.phone, me?.profile?.preferredMode])

  if (!me) return null
  const profile = me.profile
  const dirty = phone !== (profile?.phone ?? '') || mode !== (profile?.preferredMode ?? 'CLICK_TO_CALL')
  const waitingForAnswer = !!profile?.verificationRequestedAt && !profile.verified

  const save = () =>
    update.mutate(
      { phone: phone.trim() || undefined, preferredMode: mode },
      {
        onSuccess: () => toast.success('Saved'),
        onError: (error) => toast.error(getErrorMessage(error, 'Could not save your number')),
      }
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <PhoneCall className="h-5 w-5 text-lime-600" /> Your number
          {profile?.verified ? (
            <Badge variant="success">Verified</Badge>
          ) : profile?.phone ? (
            <Badge variant="warning">Not verified</Badge>
          ) : null}
        </CardTitle>
        <CardDescription>
          For click-to-call, Exotel rings this number first and then connects you to the lead. Type it with or without
          the country code ({me.defaultRegion} is assumed).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
          <div className="space-y-1.5">
            <label htmlFor="my-phone" className="text-xs font-medium text-neutral-600">Phone number</label>
            <input
              id="my-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-600" id="preferred-mode-label">Call with</label>
            <Select value={mode} onValueChange={(value) => setMode(value as CallMode)}>
              <SelectTrigger aria-labelledby="preferred-mode-label"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CLICK_TO_CALL">Click-to-call (Exotel)</SelectItem>
                <SelectItem value="SIM">My phone's SIM (app)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={!dirty || update.isPending}>Save</Button>
          {profile?.phone && !profile.verified && !dirty && me.verificationCallAvailable && (
            <Button
              variant="outline"
              disabled={startVerification.isPending}
              onClick={() =>
                startVerification.mutate(undefined, {
                  onSuccess: () => toast.info('Calling you now. Answer the call, then confirm here.'),
                  onError: (error) => toast.error(getErrorMessage(error, 'Could not start the verification call')),
                })
              }
            >
              {startVerification.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {waitingForAnswer ? 'Call me again' : 'Verify with a call'}
            </Button>
          )}
          {waitingForAnswer && (
            <Button
              variant="success"
              disabled={confirm.isPending}
              onClick={() =>
                confirm.mutate(undefined, {
                  onSuccess: () => toast.success('Your number is verified'),
                  onError: (error) => toast.error(getErrorMessage(error, 'Could not confirm')),
                })
              }
            >
              <CheckCircle2 className="mr-2 h-4 w-4" /> I answered
            </Button>
          )}
        </div>
        {profile?.phone && !profile.verified && !me.verificationCallAvailable && (
          <p className="flex items-center gap-2 text-sm text-amber-700">
            <ShieldAlert className="h-4 w-4" /> Ask an admin to verify your number in Telephony settings.
          </p>
        )}
        {!me.providerConfigured && (
          <p className="text-sm text-neutral-500">Click-to-call is not set up for this workspace yet.</p>
        )}
      </CardContent>
    </Card>
  )
}

function PhoneAppCard() {
  const { data: me } = useCallingMe()
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const native = isNativeApp()
  const [syncing, setSyncing] = useState(false)

  const { data: sims = [] } = useQuery({
    queryKey: ['native', 'sim-accounts'],
    queryFn: async () => (await SimCall.listSimAccounts()).accounts,
    enabled: native,
  })
  const { data: workSim } = useQuery({ queryKey: ['native', 'work-sim'], queryFn: () => simStorage.workSim(), enabled: native })

  if (!me) return null

  const remove = async (deviceId: string) => {
    try {
      await removeDevice(deviceId)
      await queryClient.invalidateQueries({ queryKey: callingKeys.me(workspaceId) })
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not remove the device'))
    }
  }

  const syncNow = async () => {
    setSyncing(true)
    try {
      if (!(await ensureSimPermissions())) {
        toast.error('Allow phone and call log access to sync calls')
        return
      }
      await syncCallLog(me.defaultRegion)
      toast.success('Calls synced')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Smartphone className="h-5 w-5 text-violet-600" /> Call from your phone
        </CardTitle>
        <CardDescription>
          With the Android app, calls go out on your own SIM. The app reads your call log afterwards to log the result,
          only for numbers you called from the CRM. Personal calls never leave your phone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!native && (
          me.android.apkUrl ? (
            <a
              href={me.android.apkUrl}
              className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium hover:bg-neutral-50"
            >
              <Download className="h-4 w-4" /> Download the Android app (v{me.android.latestVersion})
            </a>
          ) : (
            <p className="text-sm text-neutral-500">The Android app is not published for this workspace yet.</p>
          )
        )}

        {native && (
          <div className="space-y-3 rounded-xl border border-violet-100 bg-violet-50/50 p-3">
            {sims.length > 1 && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-neutral-600" id="work-sim-label">Work SIM</label>
                <Select
                  value={workSim ?? 'any'}
                  onValueChange={async (value) => {
                    await simStorage.setWorkSim(value === 'any' ? null : value)
                    await queryClient.invalidateQueries({ queryKey: ['native', 'work-sim'] })
                  }}
                >
                  <SelectTrigger aria-labelledby="work-sim-label"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Phone default</SelectItem>
                    {sims.map((sim) => (
                      <SelectItem key={sim.id} value={sim.id}>{sim.label}{sim.number ? ` · ${sim.number}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button variant="outline" onClick={() => void syncNow()} disabled={syncing}>
              <RefreshCw className={syncing ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} /> Sync now
            </Button>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Your devices</p>
          {me.devices.length === 0 ? (
            <p className="text-sm text-neutral-500">No phone is signed in yet. Install the app and sign in to call from your phone.</p>
          ) : (
            me.devices.map((device) => (
              <div key={device.id} className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2">
                <Smartphone className="h-4 w-4 text-neutral-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{device.deviceLabel ?? device.platform}</p>
                  <p className="text-xs text-neutral-500">
                    v{device.appVersion ?? '?'} · seen {formatDateTime(device.lastSeenAt)}
                    {!device.pushCapable && ' · notifications off'}
                  </p>
                </div>
                <Button variant="ghost" size="icon" aria-label="Remove device" onClick={() => void remove(device.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}
